/* G Decor — PDP inventory cap.
 *
 * Reads variant inventory from window.gdProductData.variants (rendered
 * server-side in theme.liquid via Liquid, so the data is accurate — the
 * /products/{handle}.js endpoint doesn't always include inventory_quantity).
 *
 * Behaviour:
 *   1. On variant change, set qty input `max` to the variant’s available stock
 *   2. If stock is low (≤ LOW_STOCK_THRESHOLD), show “Only X left in stock”
 *   3. If user types/clicks above the cap, snap back + flash warning
 *   4. If stock = 0 AND inventory_policy=deny, show a sold-out line that
 *      points to the “Notify me when back in stock” button (v2, 26-Sep-2026):
 *        - another option can still be bought → “choose another, or notify me”
 *        - nothing on the product can be bought → “sold out for now, notify me”
 *      v1 always said “please choose another design”, which was wrong on a
 *      product where every design is sold out and waiting for stock.
 *   5. If inventory isn’t tracked OR policy = continue (oversell allowed),
 *      no cap, no message — the variant behaves as before
 *   6. Fallback: if inventory data is missing entirely, do NOTHING
 *      (don’t show false out-of-stock messages)
 */
(function(){
  'use strict';
  if (!document.body.classList.contains('template-product')) return;

  var pd = window.gdProductData;
  if (!pd || !pd.variants || !Array.isArray(pd.variants) || pd.variants.length === 0) return;

  var LOW_STOCK_THRESHOLD = 10;
  var POLL_MS = 250;
  var BRASS = '#8C6F42';
  var WARN = '#a14a3a';
  var MSG_OTHERS_AVAILABLE = 'This one is sold out — choose another, or tap “Notify me” below for this one.';
  var MSG_ALL_SOLD_OUT = 'Sold out for now — tap “Notify me” below and we’ll email you the moment it’s back.';

  var variantsByID = {};
  pd.variants.forEach(function(v){ variantsByID[String(v.id)] = v; });

  var idInput = null;
  var quantityInput = null;
  var messageEl = null;
  var currentMax = null;
  var lastSeenID = null;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', ready);
  } else {
    ready();
  }

  function ready(){
    var form = document.querySelector('form[action*="/cart/add"]');
    if (!form) return;
    idInput = form.querySelector('input[name="id"]');
    quantityInput = form.querySelector('input[name="quantity"]');
    if (!idInput || !quantityInput) return;

    insertMessageEl();
    tick();
    setInterval(tick, POLL_MS);

    quantityInput.addEventListener('input', enforceCap, false);
    quantityInput.addEventListener('change', enforceCap, false);
  }

  function insertMessageEl(){
    var anchor = quantityInput.closest('.product-form__quantity, .quantity-wrap, .quantity, .product-info__qty')
              || quantityInput.parentElement;
    if (!anchor || !anchor.parentNode) return;
    messageEl = document.createElement('p');
    messageEl.className = 'gd-inv-msg';
    messageEl.setAttribute('aria-live', 'polite');
    messageEl.style.cssText = [
      "font-family:'Cormorant Garamond',Georgia,serif",
      'font-style:italic',
      'font-size:14px',
      'line-height:1.5',
      'color:' + BRASS,
      'margin:8px 0 0',
      'letter-spacing:0.01em',
      'font-weight:500',
      'display:none'
    ].join(';');
    anchor.parentNode.insertBefore(messageEl, anchor.nextSibling);
  }

  function getMaxFor(v){
    // Return null = no cap, no message (do nothing)
    if (!v) return null;
    // Variants without Shopify-tracked inventory — no cap
    if (v.inventory_management !== 'shopify') return null;
    // Oversell allowed — no cap
    if (v.inventory_policy === 'continue') return null;
    // Inventory not a number — no cap (avoid false out-of-stock)
    if (typeof v.inventory_quantity !== 'number') return null;
    return Math.max(0, v.inventory_quantity);
  }

  // Can the customer buy any OTHER option on this product right now?
  function otherOptionBuyable(currentId){
    return pd.variants.some(function(v){
      if (String(v.id) === String(currentId)) return false;
      var max = getMaxFor(v);
      return max === null || max > 0;
    });
  }

  function tick(){
    if (!idInput) return;
    var id = (idInput.value || '').trim();
    if (id !== lastSeenID) {
      lastSeenID = id;
      var v = variantsByID[String(id)];
      currentMax = getMaxFor(v);
      applyState(currentMax, id);
    } else {
      enforceCap();
    }
  }

  function applyState(max, id){
    if (!quantityInput) return;
    if (max === null) {
      quantityInput.removeAttribute('max');
      if (messageEl) messageEl.style.display = 'none';
      return;
    }
    if (max <= 0) {
      quantityInput.setAttribute('max', '0');
      if (messageEl) {
        messageEl.textContent = otherOptionBuyable(id) ? MSG_OTHERS_AVAILABLE : MSG_ALL_SOLD_OUT;
        messageEl.style.color = BRASS;
        messageEl.style.fontWeight = '500';
        messageEl.style.display = 'block';
      }
      return;
    }
    quantityInput.setAttribute('max', String(max));
    var current = parseInt(quantityInput.value, 10);
    if (!isNaN(current) && current > max) {
      quantityInput.value = max;
    }
    if (messageEl) {
      if (max <= LOW_STOCK_THRESHOLD) {
        messageEl.textContent = (max === 1)
          ? 'Only 1 left in stock'
          : 'Only ' + max + ' left in stock';
        messageEl.style.color = BRASS;
        messageEl.style.fontWeight = '500';
        messageEl.style.display = 'block';
      } else {
        messageEl.style.display = 'none';
      }
    }
  }

  function enforceCap(){
    if (currentMax === null || !quantityInput) return;
    var val = parseInt(quantityInput.value, 10);
    if (isNaN(val) || val < 1) return;
    if (currentMax === 0) return; // out of stock — don't auto-clear
    if (val > currentMax) {
      quantityInput.value = currentMax;
      flashMessage();
    }
  }

  function flashMessage(){
    if (!messageEl) return;
    var origColor = messageEl.style.color;
    var origWeight = messageEl.style.fontWeight;
    messageEl.style.color = WARN;
    messageEl.style.fontWeight = '600';
    setTimeout(function(){
      messageEl.style.color = origColor;
      messageEl.style.fontWeight = origWeight;
    }, 1100);
  }
})();
